import React from 'react';
import moment from 'moment';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useNavigate } from "react-router-dom";
import property1 from '../../../../images/property/1.jpg';
import CardFooter from '../CardFooter';

const PropertyListView = ({ propertyRows, loading, handleDelete, onSort, sortField, sortDirection }) => {
  const navigate = useNavigate();

  const handleRowClick = (e, id) => {
    // Prevent redirect if action buttons are clicked
    if (
      e.target.closest('.action-buttons') ||
      e.target.closest('.dz-footer') ||
      e.target.closest('.sortable-header')
    ) {
      return;
    }
    navigate(`/property-details/${id}`);
  };

  const handleSort = (field) => {
    if (onSort) {
      onSort(field);
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      // Show both arrows in gray for non-active fields to indicate sortability
      return (
        <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
          <i className="fa fa-caret-up" style={{ color: '#ccc', opacity: 1, display: 'block', lineHeight: 1 }} />
          <i className="fa fa-caret-down" style={{ color: '#ccc', opacity: 1, display: 'block', lineHeight: 1, marginTop: -2 }} />
        </span>
      );
    }
    
    // Show active sorting direction in blue, inactive in gray
    return (
      <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
        <i className="fa fa-caret-up" style={{ 
          color: sortDirection === 'asc' ? '#007bff' : '#ccc', 
          opacity: 1, 
          display: 'block', 
          lineHeight: 1 
        }} />
        <i className="fa fa-caret-down" style={{ 
          color: sortDirection === 'desc' ? '#007bff' : '#ccc', 
          opacity: 1, 
          display: 'block', 
          lineHeight: 1, 
          marginTop: -2 
        }} />
      </span>
    );
  };

  // const getStatusBadgeClass = (status) => {
  //   switch (status?.toLowerCase()) {
  //     case 'active': return 'bg-success';
  //     case 'pending': return 'bg-warning';
  //     case 'sold': return 'bg-info';
  //     case 'blocked': return 'bg-danger';
  //     case 'rejected': return 'bg-secondary';
  //     default: return 'bg-secondary';
  //   }
  // };

  if (loading) {
    return (
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th width="80">Image</th>
              <th>Property Details</th>
              <th width="140">Customer</th>
              <th width="120">Price</th>
              <th width="100">Type</th>
              <th width="120">Posted Date</th>
              <th width="120">Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 8 }).map((_, idx) => (
              <tr key={idx}>
                <td><Skeleton height={60} width={80} /></td>
                <td><Skeleton height={20} count={3} /></td>
                <td><Skeleton height={20} /></td>
                <td><Skeleton height={20} /></td>
                <td><Skeleton height={20} /></td>
                <td><Skeleton height={20} /></td>
                <td><Skeleton height={20} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle">
        <thead style={{ backgroundColor: '#fff' }}>
          <tr>
            <th width="80">Image</th>
            <th 
              style={{ cursor: 'pointer', userSelect: 'none' }} 
              className="sortable-header"
              onClick={() => handleSort('title')}
            >
              Property Details
              {getSortIcon('title')}
            </th>
            <th width="140">
              Customer
            </th>
            <th 
              width="120"
              style={{ cursor: 'pointer', userSelect: 'none' }} 
              className="sortable-header"
              onClick={() => handleSort('price')}
            >
              Price
              {getSortIcon('price')}
            </th>
            <th 
              width="100"
              style={{ cursor: 'pointer', userSelect: 'none' }} 
              className="sortable-header"
              onClick={() => handleSort('listingTypeId.name')}
            >
              Type
              {getSortIcon('listingTypeId.name')}
            </th>
            <th 
              width="120"
              style={{ cursor: 'pointer', userSelect: 'none' }} 
              className="sortable-header"
              onClick={() => handleSort('createdAt')}
            >
              Posted Date
              {getSortIcon('createdAt')}
            </th>
            <th width="120">Actions</th>
          </tr>
        </thead>
        <tbody>
          {propertyRows.map((item, ind) => (
            <tr 
              key={ind} 
              onClick={(e) => handleRowClick(e, item._id)}
              style={{ cursor: 'pointer' }}
              className="property-row"
            >
              <td>
                <div className="property-image-wrapper" style={{ width: 80, height: 60, borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
                  <img 
                    src={item.imageUrls[0]} 
                    alt="Property"
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover' 
                    }}
                    onError={(e) => {
                      e.target.onerror = null; 
                      e.target.src = property1; 
                    }}
                  />
                  {item.isFeatured && (
                    <div 
                      className="featured-badge"
                      style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        background: '#ff6b35',
                        color: 'white',
                        fontSize: '8px',
                        padding: '2px 4px',
                        borderRadius: '4px',
                        fontWeight: 'bold'
                      }}
                    >
                      FEATURED
                    </div>
                  )}
                  {item.isVerified && (
                    <div 
                      className="verified-badge"
                      style={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        background: '#28a745',
                        color: 'white',
                        fontSize: '8px',
                        padding: '2px 4px',
                        borderRadius: '4px',
                        fontWeight: 'bold'
                      }}
                    >
                      ✓
                    </div>
                  )}
                </div>
              </td>
              <td>
                <div className="property-details">
                  <h6 className="mb-1 fw-bold" style={{ fontSize: '15px' }}>{item.title}</h6>
                  <p className="mb-1 text-muted" style={{ fontSize: '14px' }}>
                    <i className="fa fa-map-marker-alt me-1"></i>
                    {item.address}
                  </p>
                  <div className="property-features d-flex gap-3" style={{ fontSize: '13px', color: '#666' }}>
                    {(item.numberOfBedrooms !== null && item.numberOfBedrooms !== undefined) && (
                      <span><i className="fa fa-bed me-1"></i>{item.numberOfBedrooms} Beds</span>
                    )}
                    {(item.numberOfBathrooms !== null && item.numberOfBathrooms !== undefined) && (
                      <span><i className="fa fa-bath me-1"></i>{item.numberOfBathrooms} Baths</span>
                    )}
                    {(item.areaSize !== null && item.areaSize !== undefined) && (
                      <span><i className="fa fa-expand me-1"></i>{item.areaSize} sq ft</span>
                    )}
                  </div>
                  { item?.adStatus === 'blocked' && 
                    <div
                      style={{
                        background: '#ffeaea',
                        color: '#d32f2f',
                        border: '1px solid #f44336',
                        borderRadius: '6px',
                        padding: '6px 8px',
                        marginBottom: '8px',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <svg width="14" height="14" fill="#d32f2f" viewBox="0 0 24 24">
                        <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm-1-13h2v6h-2V7zm0 8h2v2h-2v-2z"/>
                      </svg>
                      <span>Blocked: {item?.adStatusNote}</span>
                    </div>
                  }
                  { item?.adStatus === 'rejected' && 
                    <div
                      style={{
                        background: '#ffeaea',
                        color: '#d32f2f',
                        border: '1px solid #f44336',
                        borderRadius: '6px',
                        padding: '6px 8px',
                        marginBottom: '8px',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <svg width="14" height="14" fill="#d32f2f" viewBox="0 0 24 24">
                        <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm-1-13h2v6h-2V7zm0 8h2v2h-2v-2z"/>
                      </svg>
                      <span>Rejected: {item?.adStatusNote}</span>
                    </div>
                  }
                </div>
              </td>
              <td>
                <div className="customer-info d-flex align-items-center">
                  <img 
                    src={item.customerId?.profilePicture || property1}
                    alt="Customer"
                    style={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      marginRight: 10,
                      objectFit: 'cover',
                      border: '2px solid #f0f0f0'
                    }}
                    onError={(e) => {
                      e.target.onerror = null; 
                      e.target.src = property1; 
                    }}
                  />
                  <div>
                    <div className="fw-semibold" style={{ fontSize: '14px', color: '#333' }}>
                      {item.customerId?.name || 'Unknown'}
                    </div>
                    {item.customerId?.email && (
                      <div className="text-muted" style={{ fontSize: '12px' }}>
                        {item.customerId.email.length > 20 
                          ? `${item.customerId.email.substring(0, 20)}...` 
                          : item.customerId.email
                        }
                      </div>
                    )}
                    {item.customerId?.phoneNumber && (
                      <div className="text-muted" style={{ fontSize: '12px' }}>
                        {item.customerId.phoneNumber}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td>
                <h4>
                  {item?.priceUnitId?.name || 'RS'} {Number(item.price).toLocaleString()}
                </h4>
              </td>
              <td>
                <div>
                  {/* <div className="fw-semibold" style={{ fontSize: '14px' }}>{item?.propertyTypeId?.name}</div> */}
                  <span>{item?.listingTypeId?.name}</span>
                </div>
              </td>
              <td>
                <span style={{ fontSize: '14px' }}>
                  {moment(item.createdAt).format('MMM D, YYYY')}
                </span>
                <br />
                <small className="text-muted">
                  {moment(item.createdAt).format('h:mm A')}
                </small>
              </td>
              {/* <td>
                <span className={`badge ${getStatusBadgeClass(item.adStatus)}`}>
                  {item.adStatus?.charAt(0).toUpperCase() + item.adStatus?.slice(1)}
                </span>
                {(item.adStatus === 'blocked' || item.adStatus === 'rejected') && item.adStatusNote && (
                  <div className="mt-1">
                    <small className="text-danger" style={{ fontSize: '11px' }}>
                      {item.adStatusNote.length > 20 ? `${item.adStatusNote.substring(0, 20)}...` : item.adStatusNote}
                    </small>
                  </div>
                )}
              </td> */}
              <td>
                <div className="action-buttons">
                  <CardFooter handleDelete={handleDelete} id={item._id} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PropertyListView;